import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/appConfig';
import { getAuthHeaders } from '../utils/authApi';
import ConfigService from '../utils/configService';
import useLoadMore from '../utils/useLoadMore';
import LoadMoreButton from './LoadMoreButton';

const MyPosts = () => {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('reflections');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ reflections: [], stories: [], insights: [] });
  const [pendingDeleteKey, setPendingDeleteKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  const getApiUrl = (endpoint) => ConfigService.getApiUrl(endpoint);
  const endpointByType = {
    reflections: API_ENDPOINTS.REFLECTIONS,
    stories: API_ENDPOINTS.STORIES,
    insights: API_ENDPOINTS.INSIGHTS,
  };

  const getItemUrl = (type, id) => `${getApiUrl(endpointByType[type])}${id}/`;

  const updatePostInState = (type, id, updater) => {
    setData(prev => ({
      ...prev,
      [type]: prev[type].map(item => (item.id === id ? updater(item) : item)),
    }));
  };

  const removePostFromState = (type, id) => {
    setData(prev => ({
      ...prev,
      [type]: prev[type].filter(item => item.id !== id),
    }));
  };

  const getErrorMessage = async (response, fallback) => {
    const responseData = await response.json().catch(() => ({}));
    if (responseData.detail) return responseData.detail;
    if (responseData.non_field_errors?.[0]) return responseData.non_field_errors[0];

    const firstKey = Object.keys(responseData)[0];
    const firstValue = firstKey ? responseData[firstKey] : null;
    if (Array.isArray(firstValue) && firstValue[0]) {
      return `${firstKey}: ${firstValue[0]}`;
    }
    return fallback;
  };

  const handleVisibilityChange = async (type, item, nextIsPublic) => {
    const key = `visibility:${type}:${item.id}`;
    setSavingKey(key);
    setPendingDeleteKey(null);

    try {
      const response = await fetch(getItemUrl(type, item.id), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_public: nextIsPublic }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, '可见范围更新失败'));
      }

      const updatedPost = await response.json();
      updatePostInState(type, item.id, current => ({ ...current, ...updatedPost }));
      alert(nextIsPublic ? '已设为公开，其他人可以看到。' : '已设为仅自己可见。');
    } catch (err) {
      console.error('Error updating post visibility:', err);
      alert(err.message || '可见范围更新失败，请稍后重试');
    } finally {
      setSavingKey(null);
    }
  };

  const handleDeletePost = async (type, item) => {
    const key = `delete:${type}:${item.id}`;

    if (pendingDeleteKey !== key) {
      setPendingDeleteKey(key);
      return;
    }

    setSavingKey(key);
    try {
      const response = await fetch(getItemUrl(type, item.id), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, '删除失败'));
      }

      removePostFromState(type, item.id);
      setPendingDeleteKey(null);
      alert('帖子已删除。');
    } catch (err) {
      console.error('Error deleting post:', err);
      alert(err.message || '删除失败，请稍后重试');
    } finally {
      setSavingKey(null);
    }
  };

  useEffect(() => {
    const fetchMyPosts = async () => {
      if (!isAuthenticated) { setLoading(false); return; }
      setLoading(true);
      setError(null);
      try {
        const headers = getAuthHeaders();
        const [reflectionsRes, storiesRes, insightsRes] = await Promise.all([
          fetch(getApiUrl(API_ENDPOINTS.MY_POSTS.REFLECTIONS), { headers }),
          fetch(getApiUrl(API_ENDPOINTS.MY_POSTS.STORIES), { headers }),
          fetch(getApiUrl(API_ENDPOINTS.MY_POSTS.INSIGHTS), { headers })
        ]);
        const reflections = reflectionsRes.ok ? await reflectionsRes.json() : [];
        const stories = storiesRes.ok ? await storiesRes.json() : [];
        const insights = insightsRes.ok ? await insightsRes.json() : [];
        setData({ reflections, stories, insights });
      } catch (err) {
        console.error('Error fetching my posts:', err);
        setError('无法加载您的帖子，请稍后重试');
      } finally { setLoading(false); }
    };
    fetchMyPosts();
  }, [isAuthenticated]);

  const tabs = [
    { id: 'reflections', name: '我的体式反思', count: data.reflections.length },
    { id: 'stories', name: '我的故事', count: data.stories.length },
    { id: 'insights', name: '我的洞察', count: data.insights.length }
  ];

  const getFeelingEmoji = (feeling) => {
    const map = { energized: '⚡', peaceful: '😌', challenging: '💪', grounded: '🌳', focused: '🎯', enlightened: '✨', accomplished: '🏆', relaxed: '😊', grateful: '🙏' };
    return map[feeling] || '🧘';
  };

  const PostControls = ({ type, item }) => {
    const isPublic = item.is_public !== false;
    const visibilityKey = `visibility:${type}:${item.id}`;
    const deleteKey = `delete:${type}:${item.id}`;
    const isSavingVisibility = savingKey === visibilityKey;
    const isDeleting = savingKey === deleteKey;
    const isConfirmingDelete = pendingDeleteKey === deleteKey;

    return (
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-sage-100 pt-4">
        <label className="flex items-center gap-2 text-sm text-sage-700">
          <input
            type="checkbox"
            checked={isPublic}
            disabled={isSavingVisibility || isDeleting}
            onChange={(e) => handleVisibilityChange(type, item, e.target.checked)}
            className="h-4 w-4 rounded border-sage-300 text-emerald-600 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <span>其他人可见</span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            isPublic ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'
          }`}>
            {isPublic ? '公开' : '仅自己'}
          </span>
          {isSavingVisibility && <span className="text-xs text-sage-400">保存中...</span>}
        </label>

        <button
          type="button"
          disabled={isDeleting}
          onClick={() => handleDeletePost(type, item)}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
            isConfirmingDelete
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'border border-red-200 text-red-600 hover:bg-red-50'
          }`}
        >
          {isDeleting ? '删除中...' : isConfirmingDelete ? '确认删除' : '删除'}
        </button>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg">
          <svg className="w-16 h-16 mx-auto text-sage-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-xl font-semibold text-sage-700 mb-2">请先登录</h2>
          <p className="text-sage-500">登录后即可查看您的个人帖子</p>
        </div>
      </div>
    );
  }

  const renderReflectionCard = (r) => {
    // 获取体式信息，兼容不同的字段名称
    const focusedPoses = r.focused_poses || r.focusedPoses || [];
    const poseNames = focusedPoses.map(pose => {
      if (typeof pose === 'string') return pose;
      if (typeof pose === 'object') {
        return pose.chinese_name || pose.chineseName || pose.name || '';
      }
      return '';
    }).filter(name => name);

    return (
      <div key={r.id} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">{getFeelingEmoji(r.feeling)}</span>
            <span className="text-sage-700 font-medium capitalize">{r.feeling}</span>
          </div>
          <span className="text-sm text-sage-500">{new Date(r.date).toLocaleDateString('zh-CN')}</span>
        </div>
        <p className="text-sage-600 line-clamp-3 mb-3">{r.content}</p>
        {/* 显示体式信息 */}
        {poseNames.length > 0 && (
          <div className="mb-3">
            <div className="text-xs text-sage-500 mb-1">练习体式：</div>
            <div className="flex flex-wrap gap-2">
              {poseNames.map((poseName, idx) => (
                <span 
                  key={idx}
                  className="inline-block px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full"
                >
                  {poseName}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4 flex items-center space-x-4 text-sm text-sage-500">
          <span>能量: {r.energy_level}/10</span>
          <span>专注: {r.focus_level}/10</span>
          <span>柔韧: {r.flexibility_level}/10</span>
        </div>
        <PostControls type="reflections" item={r} />
      </div>
    );
  };

  const renderStoryCard = (s) => (
    <div key={s.id} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
      <h3 className="text-lg font-semibold text-sage-800 mb-2">{s.title}</h3>
      <span className="inline-block px-2 py-1 bg-sage-100 text-sage-600 text-xs rounded-full mb-3">{s.category}</span>
      <p className="text-sage-600 line-clamp-3">{s.content}</p>
      <div className="mt-4 flex items-center justify-between text-sm text-sage-500">
        <span>{new Date(s.created_at).toLocaleDateString('zh-CN')}</span>
        <span>❤️ {s.likes || 0}</span>
      </div>
      <PostControls type="stories" item={s} />
    </div>
  );

  const renderInsightCard = (i) => (
    <div key={i.id} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-sage-800">{i.title}</h3>
        {i.verified && <span className="text-green-500">✓ 已验证</span>}
      </div>
      <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-600 text-xs rounded-full mb-3">{i.type || i.insight_type}</span>
      <p className="text-sage-600 line-clamp-3">{i.description}</p>
      <div className="mt-4 text-sm text-sage-500"><span>👍 {i.helpful || 0} 人觉得有用</span></div>
      <PostControls type="insights" item={i} />
    </div>
  );

  const EmptyState = ({ type }) => (
    <div className="col-span-2 text-center py-12">
      <svg className="w-16 h-16 mx-auto text-sage-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
      <p className="text-sage-500">还没有发布任何{type}</p>
    </div>
  );

  // 当前 tab 的数据 + 渐进式分页（首批 10 条，切换 tab 自动重置）
  const currentItems = activeTab === 'reflections'
    ? data.reflections
    : activeTab === 'stories'
      ? data.stories
      : data.insights;
  const {
    visibleItems,
    hasMore,
    remaining,
    loadMore,
  } = useLoadMore(currentItems, 10, activeTab);

  const getCurrentData = () => {
    if (currentItems.length === 0) {
      if (activeTab === 'reflections') return <EmptyState type="体式反思" />;
      if (activeTab === 'stories') return <EmptyState type="故事" />;
      return <EmptyState type="洞察" />;
    }
    if (activeTab === 'reflections') return visibleItems.map(renderReflectionCard);
    if (activeTab === 'stories') return visibleItems.map(renderStoryCard);
    if (activeTab === 'insights') return visibleItems.map(renderInsightCard);
    return null;
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-sage-800 mb-2">我的帖子</h1>
        <p className="text-sage-600">查看和管理您发布的所有内容</p>
      </div>
      <div className="flex space-x-2 mb-6 border-b border-sage-200">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === tab.id ? 'text-sage-800 border-b-2 border-sage-600' : 'text-sage-500 hover:text-sage-700'}`}>
            {tab.name}
            <span className="ml-2 px-2 py-0.5 bg-sage-100 text-sage-600 text-xs rounded-full">{tab.count}</span>
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center items-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600"></div></div>
      ) : error ? (
        <div className="text-center py-12"><p className="text-red-500">{error}</p></div>
      ) : (
        <div>
          <div className="grid gap-4 md:grid-cols-2">{getCurrentData()}</div>
          <LoadMoreButton hasMore={hasMore} remaining={remaining} onClick={loadMore} />
        </div>
      )}
    </div>
  );
};

export default MyPosts;
